/** pa-quiz.js — "find your oil" quiz.
 *
 *  The section renders every question and every possible result server-side, so
 *  this file never invents product data. It moves between steps, scores the
 *  answers, reveals one result and drives the buy block.
 *
 *  Cart adds borrow the pristine fetch reference captured by
 *  snippets/pa-pristine-fetch.liquid, because cart apps on this store
 *  monkey-patch window.fetch (the same reason assets/pa-cart-offers.js does it).
 *  After the add we dispatch on:cart:change on document, which CartForm and
 *  PageHeader both listen for, then open the drawer. From there the existing
 *  cart offers take over: the upgrade banner and the free sample picker.
 */
(function () {
  'use strict';

  var QUESTIONS = ['q1', 'q2', 'q3', 'q4', 'q5'];

  /* Scoring. Q2 (what they want to change) is the heaviest signal, Q5 (aroma)
   * is the lightest and exists mostly to break ties. Any key that is missing
   * from an answer simply scores nothing. */
  var SCORES = {
    q1: {
      body:  { coyaba: 2, anani: 2, nanichi: 2 },
      face:  { caya: 3, nanichi: 2 },
      hair:  { guada: 8 },
      all:   { coyaba: 1, anani: 1, caya: 1, nanichi: 1, guada: 1 }
    },
    q2: {
      sore:   { coyaba: 5 },
      stress: { anani: 5, nanichi: 1 },
      sleep:  { anani: 5, nanichi: 2 },
      glow:   { caya: 5 },
      dry:    { nanichi: 5 },
      scalp:  { guada: 6 }
    },
    q3: {
      dry:      { nanichi: 3 },
      dull:     { caya: 3 },
      reactive: { nanichi: 2, anani: 1 },
      balanced: {}
    },
    q4: {
      morning:  { caya: 3 },
      night:    { nanichi: 2, anani: 2 },
      after:    { coyaba: 3 },
      whenever: {}
    },
    q5: {
      warm:   { coyaba: 2, guada: 1 },
      cool:   { anani: 2 },
      bright: { caya: 2 },
      sweet:  { nanichi: 2 },
      any:    {}
    }
  };

  /* Ties break toward what actually sells. Coyaba is roughly twice any other
   * oil by units, and is the safe fallback for an unscored run. */
  var DEMAND_RANK = ['coyaba', 'anani', 'caya', 'guada', 'nanichi'];

  var pristineFetch = null;

  function getFetch() {
    if (pristineFetch) return pristineFetch;
    if (typeof window.__paFetch === 'function') {
      pristineFetch = window.__paFetch;
    } else {
      pristineFetch = window.fetch.bind(window);
    }
    return pristineFetch;
  }

  function track(event, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, props || {});
      }
    } catch (e) { /* analytics must never break the quiz */ }
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100);
    var withDecimals = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = format || '${{amount}}';
    return out.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      if (name === 'amount_no_decimals' || name === 'amount_no_decimals_with_comma_separator') return noDecimals;
      if (name === 'amount_with_comma_separator') return withDecimals.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1');
      return withDecimals;
    });
  }

  function init(root) {
    if (root.__paQuizWired) return;
    root.__paQuizWired = true;

    var isEs = (root.getAttribute('data-locale') || 'en').indexOf('es') === 0;
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var shipThreshold = parseInt(root.getAttribute('data-ship-threshold'), 10) || 0;
    var cartBase = parseInt(root.getAttribute('data-cart-total'), 10) || 0;
    var wantsFreeSample = root.getAttribute('data-free-sample') === '1';
    // Mirrors the "Two oils, 10% off" automatic discount. Without this the
    // free-shipping meter would quietly overstate how close someone is: the
    // threshold is tested against the discounted subtotal, not the list price.
    var pairPct = parseInt(root.getAttribute('data-pair-discount'), 10) || 0;
    var companyId = root.getAttribute('data-klaviyo-company');
    var listId = root.getAttribute('data-klaviyo-list');

    var questionsWrap = root.querySelector('[data-pa-questions]');
    var resultsWrap = root.querySelector('[data-pa-results]');
    var steps = Array.prototype.slice.call(root.querySelectorAll('.pa-quiz__step'));
    var dotsWrap = root.querySelector('[data-pa-dots]');
    var countEl = root.querySelector('[data-pa-count]');
    var backBtn = root.querySelector('[data-pa-back]');

    var answers = {};
    var because = {};
    var history = [];
    var current = null;
    var started = false;

    var stepByName = {};
    steps.forEach(function (s) { stepByName[s.getAttribute('data-q')] = s; });

    /* A step can opt out of being asked: Q3 (skin) is skipped for anyone who
     * came in for hair, because asking a hair customer about their complexion
     * is what makes a quiz feel automated. */
    function isSkipped(name) {
      var step = stepByName[name];
      if (!step) return true;
      var q = step.getAttribute('data-skip-if-q');
      var a = step.getAttribute('data-skip-if-a');
      if (!q || !a) return false;
      return a.split(',').indexOf(answers[q]) !== -1;
    }

    function visibleQuestions() {
      return QUESTIONS.filter(function (n) { return !isSkipped(n); });
    }

    function nextAfter(name) {
      var order = visibleQuestions();
      if (name === null) return order[0];
      var i = order.indexOf(name);
      if (i === -1 || i === order.length - 1) return 'email';
      return order[i + 1];
    }

    function renderProgress() {
      if (!dotsWrap) return;
      var order = visibleQuestions().concat(['email']);
      var idx = order.indexOf(current);
      dotsWrap.innerHTML = '';
      order.forEach(function (_, i) {
        var d = document.createElement('span');
        d.className = 'pa-quiz__dot' + (i <= idx ? ' is-done' : '');
        dotsWrap.appendChild(d);
      });
      if (countEl) {
        countEl.textContent = (idx + 1) + (isEs ? ' de ' : ' of ') + order.length;
      }
    }

    function show(name) {
      steps.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-q') === name); });
      current = name;
      renderProgress();
      if (backBtn) backBtn.hidden = history.length === 0;
      var active = stepByName[name];
      if (active) {
        var legend = active.querySelector('.pa-quiz__q');
        if (legend) legend.setAttribute('tabindex', '-1');
        // Only pull focus once the person is underway, so landing on the page
        // does not yank the viewport before they have read anything.
        if (history.length && legend) legend.focus({ preventScroll: true });
      }
    }

    root.addEventListener('click', function (evt) {
      var opt = evt.target.closest('.pa-quiz__option');
      if (!opt || !root.contains(opt)) return;
      var step = opt.closest('.pa-quiz__step');
      var q = step.getAttribute('data-q');
      var a = opt.getAttribute('data-a');

      Array.prototype.forEach.call(step.querySelectorAll('.pa-quiz__option'), function (o) {
        o.classList.toggle('is-chosen', o === opt);
      });

      answers[q] = a;
      because[q] = opt.getAttribute('data-because') || '';

      if (!started) {
        started = true;
        track('quiz_started', {});
      }
      track('quiz_answered', { question: q, answer: a });

      history.push(q);
      // A beat, so the selection is visible before the step changes.
      window.setTimeout(function () { show(nextAfter(q)); }, 180);
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        var prev = history.pop();
        if (!prev) return;
        show(prev);
      });
    }

    /* ---------- scoring ---------- */

    function rank() {
      var totals = {};
      DEMAND_RANK.forEach(function (k) { totals[k] = 0; });
      QUESTIONS.forEach(function (q) {
        var a = answers[q];
        if (!a || !SCORES[q] || !SCORES[q][a]) return;
        var map = SCORES[q][a];
        Object.keys(map).forEach(function (k) {
          totals[k] = (totals[k] || 0) + map[k];
        });
      });
      var available = {};
      Array.prototype.forEach.call(resultsWrap.querySelectorAll('[data-pa-result]'), function (el) {
        available[el.getAttribute('data-pa-result')] = el;
      });
      return Object.keys(totals)
        .filter(function (k) { return available[k]; })
        .sort(function (x, y) {
          if (totals[y] !== totals[x]) return totals[y] - totals[x];
          return DEMAND_RANK.indexOf(x) - DEMAND_RANK.indexOf(y);
        });
    }

    /* ---------- result ---------- */

    function cardFor(key) {
      return resultsWrap.querySelector('[data-pa-result="' + key + '"]');
    }

    function describe(card) {
      if (!card) return null;
      var d = card.querySelector('.pa-quiz__descriptor');
      return {
        title: card.getAttribute('data-title') || '',
        descriptor: d ? d.textContent.trim() : '',
        sample: card.getAttribute('data-sample-variant') || ''
      };
    }

    function showResult(order) {
      var primary = order[0] || 'coyaba';
      var card = cardFor(primary);
      if (!card) {
        card = resultsWrap.querySelector('[data-pa-result]');
        if (!card) return;
        primary = card.getAttribute('data-pa-result');
      }

      Array.prototype.forEach.call(resultsWrap.querySelectorAll('[data-pa-result]'), function (el) {
        el.classList.toggle('is-active', el === card);
      });
      questionsWrap.hidden = true;
      resultsWrap.classList.add('is-active');

      // Why we picked it, in their own words.
      var becauseEl = card.querySelector('[data-pa-because]');
      if (becauseEl) {
        var parts = [because.q2, because.q4].filter(Boolean);
        if (parts.length) {
          var line = parts.join(isEs ? ', y ' : ', and ');
          becauseEl.textContent = line.charAt(0).toUpperCase() + line.slice(1) + '.';
        } else {
          becauseEl.hidden = true;
        }
      }

      var runnerKey = order[1];

      card.__paOrder = order;
      wireBuy(card, order);

      track('quiz_completed', { match: primary, runner_up: runnerKey || null });

      try {
        window.localStorage.setItem('pa_quiz_result', JSON.stringify({
          match: primary, order: order, answers: answers, at: Date.now()
        }));
      } catch (e) { /* private browsing */ }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('match', primary);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* older browsers */ }

      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------- buy block ---------- */

    function wireBuy(card, order) {
      var sizes = Array.prototype.slice.call(card.querySelectorAll('.pa-quiz__size'));
      var shipEl = card.querySelector('[data-pa-ship]');
      var addBtn = card.querySelector('[data-pa-add]');
      var addLabel = card.querySelector('[data-pa-add-label]');

      // Show only the add-on for this person's runner-up. Done on every pass,
      // not just the first, because a retake can produce a different one.
      var addons = Array.prototype.slice.call(card.querySelectorAll('[data-pa-addon]'));
      var addon = null;
      addons.forEach(function (el) {
        var mine = el.getAttribute('data-oil') === order[1];
        el.hidden = !mine;
        el.classList.remove('is-chosen');
        var box = el.querySelector('input');
        if (box) box.checked = false;
        if (mine) addon = el;
      });
      var addonInput = addon ? addon.querySelector('input') : null;
      card.__paAddonInput = addonInput;

      if (card.__paBuyWired) {
        if (card.__paRender) card.__paRender();
        return;
      }
      card.__paBuyWired = true;

      function chosenSize() {
        return sizes.filter(function (b) { return b.classList.contains('is-chosen'); })[0] || sizes[0];
      }

      function total() {
        var t = 0;
        var s = chosenSize();
        if (s) t += parseInt(s.getAttribute('data-price'), 10) || 0;
        var box = card.__paAddonInput;
        if (box && box.checked) t += parseInt(box.getAttribute('data-price'), 10) || 0;
        return t;
      }

      // Two oils in the bag means the pair discount applies, so the button, the
      // meter and the analytics all work from what they will actually pay.
      function netTotal() {
        var gross = total();
        var box = card.__paAddonInput;
        return (pairPct > 0 && box && box.checked)
          ? Math.round(gross * (100 - pairPct) / 100)
          : gross;
      }

      function render() {
        var t = netTotal();
        if (addLabel) {
          addLabel.textContent = (isEs ? 'añadir a la bolsa · ' : 'add to bag · ') + formatMoney(t, moneyFormat);
        }
        if (shipEl && shipThreshold > 0) {
          var combined = cartBase + t;
          if (t >= shipThreshold) {
            shipEl.textContent = isEs ? 'Envío gratis ✓' : 'Free shipping ✓';
            shipEl.classList.add('is-met');
          } else if (combined >= shipThreshold) {
            shipEl.textContent = isEs
              ? 'Envío gratis ✓, contando lo que ya tienes en la bolsa'
              : 'Free shipping ✓, counting what is already in your bag';
            shipEl.classList.add('is-met');
          } else {
            var gap = formatMoney(shipThreshold - combined, moneyFormat);
            shipEl.textContent = isEs
              ? 'Te faltan ' + gap + ' para envío gratis.'
              : gap + ' away from free shipping.';
            shipEl.classList.remove('is-met');
          }
          shipEl.hidden = false;
        }
      }

      sizes.forEach(function (btn) {
        btn.addEventListener('click', function () {
          sizes.forEach(function (b) {
            b.classList.toggle('is-chosen', b === btn);
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          render();
        });
      });

      // Delegated, so it keeps working for whichever add-on row is shown.
      card.addEventListener('change', function (evt) {
        var row = evt.target.closest('[data-pa-addon]');
        if (!row) return;
        row.classList.toggle('is-chosen', evt.target.checked);
        render();
      });

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var items = [];
          var s = chosenSize();
          if (!s) return;
          items.push({ id: parseInt(s.getAttribute('data-variant'), 10), quantity: 1 });
          var box = card.__paAddonInput;
          if (box && box.checked) {
            items.push({ id: parseInt(box.getAttribute('data-variant'), 10), quantity: 1 });
          }
          // A free 2 mL of the third-ranked oil, so the bag arrives generous.
          // The drawer's own picker lets them switch it.
          if (wantsFreeSample) {
            var third = describe(cardFor((card.__paOrder || order)[2]));
            if (third && third.sample) {
              items.push({ id: parseInt(third.sample, 10), quantity: 1 });
            }
          }

          addBtn.disabled = true;
          track('quiz_add_to_cart', {
            match: card.getAttribute('data-pa-result'),
            with_addon: !!(card.__paAddonInput && card.__paAddonInput.checked),
            value: netTotal() / 100,
            list_value: total() / 100
          });

          var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          getFetch()(window.location.origin + root_url.replace(/\/$/, '') + '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          }).then(function () {
            addBtn.disabled = false;
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
          }).catch(function () {
            // Fall back to the cart page rather than leaving them stuck.
            window.location.href = root_url.replace(/\/$/, '') + '/cart';
          });
        });
      }

      card.__paRender = render;
      render();
    }

    /* ---------- email step ---------- */

    var submitBtn = root.querySelector('[data-pa-submit]');
    var emailInput = root.querySelector('[data-pa-email]');
    var nameInput = root.querySelector('[data-pa-name]');
    var emailError = root.querySelector('[data-pa-email-error]');
    var nameError = root.querySelector('[data-pa-name-error]');

    /* The event MUST carry an explicit profile.
     *
     * Klaviyo's onsite _learnq tracking attributes every event to whichever
     * profile the browser's __kla_id cookie already holds. On a laptop that has
     * ever been identified, a quiz taken with a different address lands on the
     * OLD profile, and the flow either never fires or fires for the wrong
     * person. That is exactly what happened on 2026-09-02: an event carrying
     * $email petrapartnerships@gmail.com was recorded against
     * olandaniella@gmail.com.
     *
     * The client events endpoint takes the profile explicitly, the same way the
     * subscriptions call already does, so the email someone typed is the one
     * the event belongs to.
     */
    // Klaviyo templates are Django, not Liquid: no {% assign %}, so an email
    // cannot look up the copy for an oil. It gets finished copy here and only
    // interpolates. The blob is rendered by the section from the same settings
    // the result screen uses, so the two can never drift apart.
    var OIL_DATA = {};
    try {
      var blob = root.querySelector('[data-pa-oil-data]') ||
                 document.querySelector('[data-pa-oil-data]');
      if (blob) OIL_DATA = JSON.parse(blob.textContent);
    } catch (e) { /* the quiz still works, the emails just lose their copy */ }

    function quizProps(order) {
      var m = OIL_DATA[order[0]] || {};
      var r = OIL_DATA[order[1]] || {};
      return {
        OilMatch: order[0] || '',
        OilRunnerUp: order[1] || '',
        QuizWhere: answers.q1 || '',
        QuizGoal: answers.q2 || '',
        QuizSkin: answers.q3 || '',
        QuizWhen: answers.q4 || '',
        QuizAroma: answers.q5 || '',
        OilName: m.name || '',
        OilDesc: m.desc || '',
        OilUrl: m.url || '',
        OilImage: m.image || '',
        OilAroma: m.aroma || '',
        OilWhy: m.why || '',
        OilUse: m.use || '',
        PlantName: m.plantName || '',
        PlantLatin: m.plantLatin || '',
        PlantWhy: m.plantWhy || '',
        PlantImage: m.plantImage || '',
        RunnerName: r.name || '',
        RunnerDesc: r.desc || '',
        RunnerUrl: r.url || '',
        RunnerImage: r.image || ''
      };
    }

    function subscribe(email) {
      // Always returns a promise: the caller chains the event onto it, and
      // consent has to be recorded before the event triggers the flow.
      if (!companyId || !listId) return Promise.resolve();
      return getFetch()('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2023-02-22' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { list_id: listId, email: email }
          }
        })
      }).catch(function () { /* never block the result on Klaviyo */ });
    }

    function sendEvent(email, firstName, order) {
      if (!companyId) return Promise.resolve();
      var props = quizProps(order);
      var profileAttrs = { email: email, properties: props };
      if (firstName) profileAttrs.first_name = firstName;
      return getFetch()('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: props,
              metric: { data: { type: 'metric', attributes: { name: 'Completed Oil Quiz' } } },
              profile: { data: { type: 'profile', attributes: profileAttrs } }
            }
          }
        })
      }).catch(function () { /* never block the result on Klaviyo */ });
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        var email = (emailInput && emailInput.value || '').trim();
        var firstName = (nameInput && nameInput.value || '').trim();
        if (!firstName) {
          if (nameError) nameError.hidden = false;
          if (nameInput) nameInput.focus();
          return;
        }
        if (nameError) nameError.hidden = true;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (emailError) emailError.hidden = false;
          if (emailInput) emailInput.focus();
          return;
        }
        if (emailError) emailError.hidden = true;
        submitBtn.disabled = true;

        var order = rank();
        track('quiz_email_submitted', { match: order[0] || null });

        // Consent has to land BEFORE the Completed Oil Quiz event, because the
        // flow triggered by that event checks whether the profile can receive
        // marketing at send time. Fire the event first and a brand-new
        // subscriber can be skipped for email 1 with no error anywhere.
        // Ordered, not awaited: the result never waits on the network, so a
        // slow Klaviyo call cannot look like a broken quiz.
        try {
          subscribe(email).then(function () {
            sendEvent(email, firstName, order);
          });
        } catch (e) {
          // Whatever went wrong with Klaviyo, they still get their result.
        }
        showResult(order);
      });
    }

    /* ---------- deep link ---------- */

    // /pages/find-your-oil?match=coyaba lets the results email link back and
    // lets an ad point straight at a result.
    try {
      var deep = new URLSearchParams(window.location.search).get('match');
      if (deep && cardFor(deep)) {
        var order = [deep].concat(DEMAND_RANK.filter(function (k) { return k !== deep; }));
        showResult(order);
      }
    } catch (e) { /* no URLSearchParams */ }

    /* ---------- retake ---------- */

    root.addEventListener('click', function (evt) {
      var btn = evt.target.closest('[data-pa-retake]');
      if (!btn || !root.contains(btn)) return;
      answers = {};
      because = {};
      history = [];
      started = false;
      Array.prototype.forEach.call(root.querySelectorAll('.pa-quiz__option.is-chosen'), function (o) {
        o.classList.remove('is-chosen');
      });
      Array.prototype.forEach.call(resultsWrap.querySelectorAll('[data-pa-result]'), function (el) {
        el.classList.remove('is-active');
      });
      resultsWrap.classList.remove('is-active');
      questionsWrap.hidden = false;
      if (submitBtn) submitBtn.disabled = false;
      try {
        var url = new URL(window.location.href);
        url.searchParams.delete('match');
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* older browsers */ }
      show('q1');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    show('q1');
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pa-quiz]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
